import { useState } from "react"
import { Link as RouterLink } from "react-router-dom"
import { Alert, Button, Link, Stack, TextField, Typography } from "@mui/material"
import { useAuth } from "../context/AuthContext"
import AuthLayout from "./AuthLayout"

const SESSION_ENDED_MESSAGES = {
  SESSION_INVALID: "Your session was ended. This can happen when your role changes or you sign out elsewhere.",
  TOKEN_EXPIRED: "Your session expired. Please sign in again.",
  TOKEN_INVALID: "Your session is no longer valid. Please sign in again.",
}

export default function Login() {
  const { login, sessionEndedReason } = useAuth()
  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)

  const submit = async (event) => {
    event.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await login(username.trim(), password)
    } catch (err) {
      // The server answers "Invalid username or password" for both a missing
      // account and a wrong password. Passing it through unchanged is the point:
      // distinguishing them here would rebuild the enumeration oracle the server
      // is careful not to be.
      setError(err.userMessage || "Could not sign in")
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthLayout
      subtitle="Financials platform"
      footer={
        <Typography variant="caption" color="text.secondary">
          Trouble signing in? Contact your TallyWise administrator.
        </Typography>
      }
    >
      {sessionEndedReason && !error ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {SESSION_ENDED_MESSAGES[sessionEndedReason] || "Please sign in again."}
        </Alert>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <form onSubmit={submit}>
        <Stack spacing={2}>
          {/* Username, not email. One mailbox often covers a whole team, with an
              account each — so an address does not pick out a person, and asking
              for one here would be asking a question with several answers. */}
          <TextField
            label="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            autoFocus
            required
            fullWidth
          />
          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            fullWidth
          />
          <Button type="submit" variant="contained" size="large" disabled={busy} fullWidth>
            {busy ? "Signing in…" : "Sign in"}
          </Button>
          {/* Brand dark, not brand green. #7ed348 on white is about 1.8:1 —
              the link was there the whole time and nobody could see it. */}
          <Link
            component={RouterLink}
            to="/forgot-password"
            variant="body2"
            align="center"
            sx={{
              color: "primary.dark",
              fontWeight: 600,
              textDecoration: "underline",
              textUnderlineOffset: 3,
            }}
          >
            Forgot password?
          </Link>
        </Stack>
      </form>
    </AuthLayout>
  )
}
